import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { SelectHTMLAttributes, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import ChevronDownIcon from '@app/assets/icons/chevron-down.svg?react';
import clsx from 'clsx';

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  placeholder?: string;
  options: { value: string; label: string; [key: string]: any }[];
  showDefaultOption?: boolean;
  sizeStyle?: 'sm' | 'md' | 'lg';
  label?: string;
  isOptional?: boolean;
  bgClass?: string;
}

const getStyle = (size: string) => {
  switch (size) {
    case 'sm': {
      return 'text-sm py-2 px-3';
    }
    case 'md': {
      return 'text-normal py-3 px-4';
    }
    case 'lg': {
      return 'text-lg py-4 px-5';
    }
  }
};

export const SelectField = ({
  name,
  placeholder,
  options,
  showDefaultOption = false,
  sizeStyle = 'md',
  label,
  isOptional,
  ...rest
}: SelectFieldProps) => {
  const {
    control,
    // @ts-expect-error this are need to update the input state on errors/etc
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    formState: { errors, isDirty },
  } = useFormContext();

  const [query, setQuery] = useState('');

  return (
    <Controller
      control={control}
      name={name ?? ''}
      defaultValue={rest.defaultValue ?? ''}
      render={({ field: { onChange, value } }) => {
        return (
          <div className="relative w-full">
            <Combobox
              immediate
              value={value ?? ''}
              onChange={(e) => {
                if (rest.onSelect) {
                  rest.onSelect(e);
                }
                onChange(e);
              }}
              onClose={() => setQuery('')}
            >
              {({ activeOption }) => (
                <>
                  {label && (
                    <label
                      htmlFor={name}
                      className="font-medium text-sm inline-block mb-1"
                    >
                      {label}{' '}
                      {isOptional && (
                        <span className="text-base-coolGray">(Optional)</span>
                      )}
                    </label>
                  )}
                  <div className="relative w-full">
                    <div className="relative w-full">
                      <ComboboxInput
                        displayValue={(item) =>
                          options?.find((o) => o.value === item)?.label ??
                          activeOption?.label ??
                          ''
                        }
                        onChange={(event) => setQuery(event.target.value ?? '')}
                        placeholder={placeholder}
                        className={clsx(
                          `group ${rest.bgClass ?? 'bg-base-seasalt'} rounded py-3 px-4 w-full outline-brand-baseBlue outline-1 ${rest.className ?? ''} ${getStyle(sizeStyle)} pr-10`,
                          { 'border border-system-error': name && errors[name] }
                        )}

                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                  <ComboboxOptions
                    className="absolute left-0 right-0 z-[9999] w-full mt-1 rounded-lg shadow-lg bg-white max-h-64 overflow-y-auto border border-gray-200"
                  >
                    {showDefaultOption && (
                      <option
                        value="null"
                        disabled
                        className="data-[focus]:bg-brand-lightBlue4 p-4 text-sm"
                      >
                        Choose an option
                      </option>
                    )}
                    {query.length > 0 &&
                      options
                        .filter((option) =>
                          option?.label
                            ?.toLowerCase()
                            .includes(query.toLowerCase())
                        )
                        .map((option) => (
                          <ComboboxOption
                            key={option.value}
                            value={option.value}
                            className="data-[focus]:bg-brand-lightBlue4 p-4 text-sm border-b border-b-base-seasalt"
                          >
                            {option.label}
                          </ComboboxOption>
                        ))}
                    {(!query || query.length === 0) &&
                      options?.map((option) => (
                        <ComboboxOption
                          key={option.value}
                          value={option.value}
                          className="data-[focus]:bg-brand-lightBlue4 p-4 text-sm border-b border-b-base-seasalt"
                        >
                          {option.label}
                        </ComboboxOption>
                      ))}
                  </ComboboxOptions>
                </>
              )}
            </Combobox>
            {name && errors[name] && (
              <p role="alert" className="text-system-error">
                <p
                  role="alert"
                  className="text-system-error text-sm text-left pt-1"
                >
                  {typeof errors?.[name]?.message === 'string'
                    ? errors[name]?.message
                    : ''}
                </p>
              </p>
            )}
          </div>
        );
      }}
    />
  );
};
